import { OpenAPI as OpenAPIv3_0, Operation as OperationV3_0, Parameter as ParameterV3_0 } from '@criteria/openapi/v3.0'
import { OpenAPI as OpenAPIv3_1, Operation as OperationV3_1, Parameter as ParameterV3_1 } from '@criteria/openapi/v3.1'
import { RequestDescriptorProvider } from 'criteria-requests-ui'
import { ReactNode, createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useActiveDocument } from '../document-view/useActiveDocument'
import { OpenAPIDocument } from '../documents/OpenAPIDocument'
import { useAuthorizationStore } from './authorization-store'
import { requestWithAuthorizations } from './requestFromAuthorizations'

type OpenAPI = OpenAPIv3_1<never> | OpenAPIv3_0<never>
type Operation = OperationV3_1<never> | OperationV3_0<never>
type Parameter = ParameterV3_1<never> | ParameterV3_0<never>
type Server = { url: string }

const isURL = (value: string) => {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

const APIExplorerContext = createContext<{
  namespace: string
  openAPI?: OpenAPI
  server: Server
  setServer: (server: Server) => void
  operation: (Operation & { path: `/${string}`; method: string; operationRef: string }) | null
  setOperation: (operation: Operation & { path: `/${string}`; method: string; operationRef: string }) => void
}>({
  namespace: '',
  server: { url: 'https://example.com' },
  setServer: () => {},
  operation: null,
  setOperation: () => {}
})

export function useAPIExplorer() {
  return useContext(APIExplorerContext)
}

export function APIExplorerProvider({ children }: { children?: ReactNode }) {
  const activeDocument = useActiveDocument()
  const activeOpenAPIDocument: OpenAPIDocument | null =
    activeDocument instanceof OpenAPIDocument ? (activeDocument as OpenAPIDocument) : null

  const openAPI = useSyncExternalStore(
    (callback) => {
      activeOpenAPIDocument?.addListener(callback)
      return () => activeOpenAPIDocument?.removeListener(callback)
    },
    () => {
      activeOpenAPIDocument?.reloadContent().catch(console.error)
      return activeOpenAPIDocument?.openAPI
    },
    () => {
      return undefined
    }
  )

  const namespace = activeOpenAPIDocument?.path ?? ''

  const [server, setServer] = useState<Server | null>(null)
  useEffect(() => {
    setServer(openAPI?.servers && openAPI.servers.length > 0 ? openAPI.servers[0] : null)
  }, [openAPI])

  const [operation, setOperation] = useState<
    (Operation & { path: `/${string}`; method: string; operationRef: string }) | null
  >(null)
  useEffect(() => {
    setOperation(null)
  }, [namespace])

  const authorizations = useAuthorizationStore()
  const securityRequest =
    openAPI && operation ? requestWithAuthorizations(namespace, authorizations, openAPI, operation) : null

  const pathItem = openAPI && operation ? (openAPI.paths ?? {})[operation.path] : null

  const { parameters: pathItemParameters = [] } = pathItem ?? {}
  const { parameters: operationParameters = [] } = operation ?? {}
  const parameters: Parameter[] = [
    ...pathItemParameters,
    ...(operationParameters as Parameter[]).filter((operationParameter) =>
      (pathItemParameters as Parameter[]).every(
        (pathItemParameter) =>
          operationParameter.name !== pathItemParameter.name || operationParameter.in !== pathItemParameter.in
      )
    )
  ]

  const pathParameters: Record<string, string> = {}
  parameters.forEach((parameter) => {
    if (parameter.in === 'path') {
      pathParameters[parameter.name] = ''
    }
  })

  const urlSearchParams = new URLSearchParams()
  parameters.forEach((parameter) => {
    if (parameter.in === 'query') {
      urlSearchParams.append(parameter.name, '')
    }
  })

  const headers = new Headers()
  parameters.forEach((parameter) => {
    if (parameter.in === 'header') {
      headers.append(parameter.name, '')
    }
  })

  const serverURL = server && isURL(server.url) ? server.url : null
  const operationPath = operation?.path ?? null
  const path = useMemo(() => {
    if (!operationPath) {
      return serverURL ?? 'https://example.com'
    }
    if (!serverURL) {
      return operationPath ?? 'https://example.com'
    }
    const serverPathname = new URL(serverURL).pathname
    if (serverPathname.endsWith('/')) {
      return serverPathname.slice(0, -1) + operationPath
    } else {
      return serverPathname + operationPath
    }
  }, [serverURL, operationPath])

  const operationRequest = {
    url: {
      base: serverURL ?? 'https://example.com',
      path,
      parameters: Object.keys(pathParameters).length > 0 ? pathParameters : undefined,
      query: urlSearchParams.size > 0 ? urlSearchParams.toString() : undefined
    },
    method: operation?.method,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    resourceConfiguration: {
      supportedMediaTypes: Object.keys(operation?.requestBody?.content ?? {})
    }
  }

  return (
    <APIExplorerContext.Provider
      value={{
        namespace,
        openAPI,
        server: server ?? { url: 'https://example.com' },
        setServer,
        operation,
        setOperation
      }}
    >
      <RequestDescriptorProvider value={securityRequest ?? undefined}>
        <RequestDescriptorProvider value={operationRequest}>{children}</RequestDescriptorProvider>
      </RequestDescriptorProvider>
    </APIExplorerContext.Provider>
  )
}
